import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Search, ShieldCheck, AlertCircle, Award, Calendar, User, GraduationCap } from "lucide-react";

export default function VerifyCertificate() {
  const [regNo, setRegNo] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!regNo.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/certificates/verify`, { params: { registration_no: regNo.trim() } });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Certificate not found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="verify" className="py-24 bg-[#F9F8F6] relative" data-testid="verify-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="overline text-[#E07A5F]">Authenticity Check</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1B4332] mt-3">
            Verify Your Certificate Online
          </h2>
          <p className="mt-4 text-[#5C6661] leading-relaxed">
            Employers and institutions can easily verify the authenticity of certificates
            issued by our foundation. Enter the registration number to get instant verification.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-[#5C6661]">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#2D6A4F]"/> Instant verification</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#2D6A4F]"/> Detailed info</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#2D6A4F]"/> Secure & reliable</span>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-border rounded-2xl p-3 flex flex-col sm:flex-row gap-3 shadow-sm max-w-2xl mx-auto"
          data-testid="certificate-verification-form"
        >
          <div className="flex-1 flex items-center gap-3 px-4">
            <Search className="w-5 h-5 text-[#5C6661] shrink-0" />
            <input
              type="text"
              value={regNo}
              onChange={(e) => setRegNo(e.target.value)}
              placeholder="Enter Registration Number (e.g. EDF2024001)"
              className="w-full py-3 outline-none text-sm bg-transparent placeholder:text-[#9CA3AF]"
              data-testid="verify-registration-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !regNo.trim()}
            className="bg-[#1B4332] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-[#2D6A4F] transition-colors disabled:opacity-50"
            data-testid="verify-certificate-btn"
          >
            {loading ? "Verifying..." : "Verify Now"}
          </button>
        </form>

        {error && (
          <div className="mt-6 max-w-2xl mx-auto bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-start gap-3" data-testid="verify-error">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm">Certificate not found</div>
              <div className="text-xs mt-1">{error}</div>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-10 max-w-3xl mx-auto fade-in" data-testid="verify-result">
            <div className="relative bg-white border-2 border-[#1B4332]/10 rounded-2xl overflow-hidden shadow-lg">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#E07A5F] via-[#F4A261] to-[#1B4332]" />
              <div className="p-8 sm:p-10">
                <div className="flex items-center gap-2 text-[#2D6A4F]">
                  <div className="w-10 h-10 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">Verified Certificate</div>
                    <div className="text-xs text-[#5C6661]">Issued by EDSW Foundation</div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-8 mt-8">
                  <div className="flex flex-col items-center sm:items-start">
                    {result.photo ? (
                      <img src={result.photo} alt={result.student_name} className="w-32 h-32 rounded-2xl object-cover border border-border" />
                    ) : (
                      <div className="w-32 h-32 rounded-2xl bg-[#F2EFEB] flex items-center justify-center border border-border">
                        <User className="w-12 h-12 text-[#5C6661]" />
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-2 space-y-3">
                    <Row icon={User} label="Student Name" value={result.student_name} />
                    {result.father_name && <Row icon={User} label="Father's Name" value={result.father_name} />}
                    <Row icon={GraduationCap} label="Course" value={result.course} />
                    {result.duration && <Row icon={Calendar} label="Duration" value={result.duration} />}
                    <Row icon={Award} label="Grade" value={result.grade} />
                    <Row icon={Calendar} label="Issue Date" value={result.issue_date} />
                    <Row icon={ShieldCheck} label="Registration No." value={result.registration_no} mono />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-[#E07A5F] mt-1 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#5C6661]">{label}</div>
        <div className={`text-[#222B26] font-medium ${mono ? "font-mono text-sm" : ""}`}>{value}</div>
      </div>
    </div>
  );
}
